import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { CheckCircle, Error, LockOpen } from '@mui/icons-material';
import axiosInstance from '../services/axios';

const UnlockApprovePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  
  const interviewId = searchParams.get('interview');

  useEffect(() => {
    if (!interviewId) {
      setStatus('error');
      setErrorMessage('Invalid unlock request - missing interview ID');
      setIsLoading(false);
      return;
    }

    // Auto-approve the unlock request
    approveUnlock();
  }, [interviewId]);

  const approveUnlock = async () => {
    if (!interviewId) return;
    
    try {
      const response = await axiosInstance.post(`/interviews/${interviewId}/unlock-approve`);

      if (response.status === 200) {
        // Success - redirect to success page  
        navigate(`/manager/unlock-success?interview=${interviewId}`);
      } else {
        setStatus('error');
        setErrorMessage('Failed to approve unlock request');
      }
    } catch (error: any) {
      setStatus('error');
      if (error.response?.status === 302) {
        // Backend sent redirect - follow it to success page
        navigate(`/manager/unlock-success?interview=${interviewId}`);
      } else if (error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage('Network error - please check your connection');
      }
    }
    
    setIsLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <LockOpen sx={{ fontSize: 60, color: 'primary.main' }} />
          
          <Typography variant="h4" component="h1" textAlign="center" gutterBottom>
            Interview Unlock Request
          </Typography>

          {isLoading && (
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Processing unlock approval...
                </Typography>
                <Typography color="text.secondary">
                  Please wait while we approve the interview unlock request.
                </Typography>
              </CardContent>
            </Card>
          )}

          {status === 'error' && (
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent>
                <Alert 
                  severity="error" 
                  icon={<Error />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" gutterBottom>
                    Approval Failed
                  </Typography>
                  <Typography>
                    {errorMessage}
                  </Typography>
                </Alert>
                
                <Box display="flex" gap={2} justifyContent="center" mt={3}>
                  <Button 
                    variant="contained" 
                    onClick={() => navigate('/dashboard')}
                  >
                    Return to Dashboard
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    onClick={() => window.location.reload()}
                  >
                    Try Again
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default UnlockApprovePage;